import { ChangeEvent, FC, useEffect, useState } from 'react';
import { Box } from '@chakra-ui/react';
import * as yup from 'yup';

import { UpdateSCVersionAudioValidations } from 'types/releases';
import { InputField } from 'app/components/Core/ChakraInputField/InputField';
import { HelpTooltip } from 'app/components/HelpTooltip';

interface IsrcFieldProps {
  disabled: boolean;
  validations?: UpdateSCVersionAudioValidations;
  isrc: string;
  saveIsrc: (isrc: string) => Promise<void>;
}

export const IsrcField: FC<IsrcFieldProps> = ({
  disabled,
  validations,
  isrc,
  saveIsrc,
}) => {
  const [value, setValue] = useState(isrc);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const isDirty = isrc !== value;
  const schema = validations?.isrc.pattern
    ? yup
        .string()
        .matches(new RegExp(validations.isrc.pattern), 'Invalid ISRC')
        .nullable()
        .transform((value) => value || null)
    : yup.string();

  const validate = (value: string) => {
    schema
      .validate(value)
      .then(() => {
        if (error) {
          setError('');
        }
      })
      .catch((error) => setError(error.message));
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    setValue(e.target.value);
    validate(e.target.value);
  };

  const onBlur = async () => {
    if (!error && isDirty) {
      setLoading(true);
      await saveIsrc(value);
      setLoading(false);
    }
  };

  useEffect(() => {
    setValue(isrc);
    validate(isrc);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isrc]);

  return (
    <Box display="flex" flexDirection="column">
      <InputField
        value={value}
        label="ISRC"
        isDisabled={disabled || loading}
        onChange={onChange}
        onBlur={onBlur}
        data-testid="ISRC"
        errorMessage={error}
        isInvalid={Boolean(error)}
        errorMessageProps={{
          'data-testid': 'isrc-error-text',
        }}
        rightElement={
          <HelpTooltip label="Leaving this field blank will automatically generate a new ISRC upon Send/Export" />
        }
      />
    </Box>
  );
};
